import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Boxes, RefreshCw, Search } from "lucide-react";
import { fmtQty } from "@/lib/constants";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";

const KINDS = ["all", "Raw Material", "Brought Out", "Mfg. Spares", "Manufactured"];
const KIND_STYLE = {
  "Raw Material": "bg-blue-100 text-blue-700",
  "Brought Out": "bg-yellow-100 text-yellow-700",
  "Mfg. Spares": "bg-purple-100 text-purple-700",
  "Manufactured": "bg-emerald-100 text-emerald-700",
};
const STATE_STYLE = {
  "In Stock": "bg-emerald-100 text-emerald-700",
  "Low": "bg-amber-100 text-amber-700",
  "Out of Stock": "bg-red-100 text-red-700",
  "Fully Reserved": "bg-rose-100 text-rose-700",
  "In Production": "bg-indigo-100 text-indigo-700",
  "At Vendor": "bg-orange-100 text-orange-700",
};

export default function GlobalInventory({ facility = "H. K. INDUSTRIES" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/inventory/global?facility=${encodeURIComponent(facility)}`).then(setData).catch(() => setData({ items: [], counts: {} })).finally(() => setLoading(false));
  }, [facility]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.items || []).filter((r) =>
    (kind === "all" || r.kind === kind) &&
    (!q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.sku || "").toLowerCase().includes(q.toLowerCase())));
  const { pageItems, pagerProps } = usePager(rows);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="global-inventory">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Boxes className="h-4 w-4 text-indigo-600" /> Global Inventory</h3>
          <p className="text-xs text-slate-400">Every part's live state — on-hand, in production, and out at vendors.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part…" className="h-8 pl-8 text-xs w-44" data-testid="gi-search" />
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} data-testid="gi-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>
      <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-slate-100">
        {KINDS.map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`gi-kind-${k}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${kind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {k === "all" ? "All parts" : k}{k !== "all" && data?.counts?.[k] != null ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>
      <div className={DT.wrap}>
        <table className={`${DT.table} min-w-[900px]`}>
          <thead className={DT.thead}>
            <tr className={DT.headerRow}>
              {["Part", "Type", "On-hand", "Reserved", "Available", "In Production", "At Vendor", "Status"].map((h) => <th key={h} className={DT.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">{loading ? "Loading…" : "No parts match."}</td></tr>}
            {pageItems.map((r) => (
              <tr key={r.id} className={DT.row} data-testid={`gi-row-${r.sku}`}>
                <td className="px-4 py-3"><p className="font-medium text-slate-800">{r.name}</p><p className="text-[11px] text-slate-400 font-mono-data">{r.sku}</p></td>
                <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${KIND_STYLE[r.kind] || "bg-slate-100 text-slate-600"}`}>{r.kind}</span></td>
                <td className="px-4 py-3 font-mono-data text-slate-700">{fmtQty(r.on_hand, r.unit)} {r.unit}</td>
                <td className={`px-4 py-3 font-mono-data ${r.reserved ? "text-rose-600" : "text-slate-400"}`}>{r.reserved ? `${fmtQty(r.reserved, r.unit)} ${r.unit}` : "—"}</td>
                <td className={`px-4 py-3 font-mono-data ${r.available <= 0 ? "text-red-500 font-semibold" : "text-slate-700"}`}>{fmtQty(r.available, r.unit)} {r.unit}</td>
                <td className="px-4 py-3 font-mono-data text-slate-500">{r.in_production ? `${fmtQty(r.in_production, r.unit)} ${r.unit}` : "—"}</td>
                <td className="px-4 py-3 font-mono-data text-slate-500">{r.with_vendor ? `${fmtQty(r.with_vendor, r.unit)} ${r.unit}` : "—"}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-mono-data uppercase px-2 py-1 rounded ${STATE_STYLE[r.state] || "bg-slate-100 text-slate-600"}`}>{r.state}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        <ListPagination {...pagerProps} testid="gi-pagination" />
      </div>
    </div>
  );
}
